import { Component, OnInit } from '@angular/core';
import { PopoverRef } from '../popover/popover-ref';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
})
export class ResetPasswordComponent implements OnInit {
    submitDisabled = true;
    oldPassword = '';
    firstNewPassword = '';
    secondNewPassword = '';

    form = new UntypedFormGroup({
        oldPassword: new UntypedFormControl('', [Validators.required]),
        newPassword1: new UntypedFormControl('', [Validators.required]),
        newPassword2: new UntypedFormControl('', [Validators.required]),
    });

    passwordRegex = /^(?=.*?[A-Z])(?!.*[ßüöäÜÖÄ])(?=.*?[0-9]).{8,}$/;
    currentPasswordScore = 0;

    private zxcvbnLocalOptions = {
        graphs: zxcvbnCommonPackage.adjacencyGraphs,
        dictionary: {
            ...zxcvbnCommonPackage.dictionary,
        },
    };

    constructor(
        public popoverRef: PopoverRef,
        private translate: TranslateService
    ) {
        if (this.popoverRef) {
            this.initializePopoverData();
        }
    }

    ngOnInit() {
        this.initialize();
    }

    close(value = false): void {
        this.popoverRef.close(value);
    }

    /**
     * Form Submit event
     */
    submitChange(): void {
        this.popoverRef.close({
            success: true,
            values: {
                oldPassword: this.oldPassword,
                firstNewPassword: this.firstNewPassword,
                secondNewPassword: this.secondNewPassword,
            },
        });
    }

    /**
     * Returns the text for the password score rating
     */
    determinePasswordScoreRatingText(): string {
        if (this.currentPasswordScore <= 2) {
            return this.translate.instant('common.unsecure');
        } else if (this.currentPasswordScore === 3) {
            return this.translate.instant('common.secure');
        }
        return this.translate.instant('common.verySafe');
    }

    /**
     * Returns the color for the password score rating
     * @param index
     */
    determinePasswordStrengthIndicatorColor(index): string {
        if (index <= this.currentPasswordScore) {
            return this.determinePasswordStrengthColor();
        }
        return 'transparent';
    }

    /**
     * Returns the color for the password score rating
     */
    determinePasswordStrengthColor(): string {
        const colors = ['#b80000', '#ea1b0a', '#f05548', '#e3e000', '#1ea2b1'];
        return colors[this.currentPasswordScore];
    }

    /**
     * Dumb-Validates the input
     * @private
     */
    private validateInput(): void {
        // tslint:disable-next-line:max-line-length
        if (
            this.oldPassword === '' ||
            this.firstNewPassword === '' ||
            this.secondNewPassword === ''
        ) {
            this.submitDisabled = true;
            return;
        }
        if (this.firstNewPassword === this.secondNewPassword) {
            this.submitDisabled = !this.passwordRegex.test(this.firstNewPassword);
            return;
        }
        this.submitDisabled = true;
    }

    /**
     * Initializes the popover data
     * @private
     */
    private initializePopoverData() {
        this.popoverRef.overlay.backdropClick().subscribe(() => {
            this.close(false);
        });
    }

    /**
     * Initializes the component
     * @private
     */
    private initialize(): void {
        zxcvbnOptions.setOptions(this.zxcvbnLocalOptions);
        this.form.valueChanges.subscribe((valueChange) => {
            this.oldPassword = valueChange.oldPassword;
            this.firstNewPassword = valueChange.newPassword1;
            if (this.firstNewPassword && this.firstNewPassword !== '') {
                this.currentPasswordScore = zxcvbn(this.firstNewPassword).score;
            }
            this.secondNewPassword = valueChange.newPassword2;
            this.validateInput();
        });
    }
}
